import React, { useState } from "react";
import { Popover, Typography, Button, Box, Divider } from "@mui/material";

const CONFIRM_STYLES = {
  success: {
    background: "linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)",
    hoverBackground: "linear-gradient(135deg, #27ae60 0%, #1e8449 100%)",
    activeBackground: "linear-gradient(135deg, #27ae60 0%, #1e8449 100%)",
    shadow: "0 2px 8px rgba(46,204,113,0.35)",
    hoverShadow: "0 4px 14px rgba(46,204,113,0.45)",
    activeShadow: "0 2px 6px rgba(46,204,113,0.3)",
  },
  error: {
    background: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
    hoverBackground: "linear-gradient(135deg, #c0392b 0%, #96281b 100%)",
    activeBackground: "linear-gradient(135deg, #c0392b 0%, #96281b 100%)",
    shadow: "0 2px 8px rgba(231,76,60,0.35)",
    hoverShadow: "0 4px 14px rgba(231,76,60,0.45)",
    activeShadow: "0 2px 6px rgba(231,76,60,0.3)",
  },
};

export default function Popconfirm({
  children,
  title = "Are you sure?",
  onConfirm,
  onCancel,
  confirmText = "Yes",
  cancelText = "No",
  confirmColor = "error",
  placement = "bottom",
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleConfirm = () => { onConfirm?.(); handleClose(); };
  const handleCancel = () => { onCancel?.(); handleClose(); };

  const styles = CONFIRM_STYLES[confirmColor] ?? CONFIRM_STYLES.error;

  return (
    <>
      <span onClick={handleOpen}>{children}</span>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: placement === "top" ? "top" : "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: placement === "top" ? "bottom" : "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              border: "1px solid rgba(0,0,0,0.06)",
              overflow: "hidden",
              minWidth: 200,
            },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e", lineHeight: 1.4 }}
          >
            {title}
          </Typography>
        </Box>

        <Divider sx={{ mt: 1, borderColor: "rgba(0,0,0,0.06)" }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 1.5, py: 1 }}>
          <Button
            size="small"
            onClick={handleCancel}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.8rem",
              color: "#6b7280",
              borderRadius: "8px",
              px: 1.5,
              "&:hover": { backgroundColor: "#f3f4f6", color: "#374151" },
            }}
          >
            {cancelText}
          </Button>

          <Button
            size="small"
            onClick={handleConfirm}
            disableElevation
            confirmColor="Approve"
            variant="contained"
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.75rem",
              px: 1.5,
              py: 0.5,
              background: "linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)",
              color: "#fff",
              boxShadow: "0 2px 8px rgba(46,204,113,0.35)",
              border: "none",
              transition: "transform 0.15s ease, box-shadow 0.15s ease",
              "&:hover": {
                  background: "linear-gradient(135deg, #27ae60 0%, #1e8449 100%)",
                  boxShadow: "0 4px 14px rgba(46,204,113,0.45)",
                  transform: "translateY(-1px)",
              },
              "&:active": { transform: "translateY(0)", boxShadow: "0 2px 6px rgba(46,204,113,0.3)" },
          }}
        >
            {confirmText}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
